import * as React from "react";

import { useAIChat } from "@/Chat/ChatSidebar/AIChatContext";
import { ChatFeedback } from "@/Chat/ChatSidebar/ChatByIdView/ChatMessage/ChatFeedback";
import { ChatMessageComponents } from "@/Chat/ChatSidebar/ChatByIdView/ChatMessage/ChatMessageComponents.tsx";
import { ThinkingIndicator } from "@/shared/components";
import {
  ChatMessage,
  ChatMessageStatus,
  ChatRecord,
} from "@/shared/domain/chat";
import {
  ChatRole,
  ChatStatus,
  isChatTextContent,
  isChatToolUseContent,
} from "@/shared/domain/chat/ChatRecord.ts";

import styles from "./ChatByIdView.module.css";
import { ScrollToBottomButton } from "./ScrollToBottomButton";
import { useScrollSentinel } from "./useScrollSentinel";
import { useSpacer } from "./useSpacer";

const AUTONAV_MAX_AGE_MS = 2 * 60 * 1000; // 2 minutes

/**
 * Check if a message is recent enough for autonav.
 * Messages without a created timestamp (e.g., in-flight) are considered recent.
 */
function isMessageRecentForAutoNav(message: ChatMessage): boolean {
  if (!message.created) {
    return true;
  }
  const messageTime = new Date(message.created).getTime();
  const now = Date.now();
  return now - messageTime < AUTONAV_MAX_AGE_MS;
}

/**
 * Determines if feedback should be shown after a message at the given index.
 * Feedback is shown after assistant messages that end with text content when:
 * 1. The next message is from the user (historical feedback)
 * 2. It's the last message and it's the user's turn (current turn feedback)
 */
function shouldShowFeedback(
  chat: ChatRecord,
  messageIndex: number,
): { show: boolean; lastAssistantText?: string } {
  const message = chat.messages[messageIndex];
  const nextMessage = chat.messages[messageIndex + 1];
  const isLastMessage = messageIndex === chat.messages.length - 1;

  // Only show feedback after assistant messages
  if (message.role !== ChatRole.ASSISTANT) {
    return { show: false };
  }

  // Must have content ending with text
  if (message.content.length === 0) {
    return { show: false };
  }

  const lastContent = message.content[message.content.length - 1];
  if (!isChatTextContent(lastContent)) {
    return { show: false };
  }

  // Show if next message is from user, or if it's the last message and user's turn
  const showAfterHistory = nextMessage && nextMessage.role === ChatRole.USER;
  const showAtEnd = isLastMessage && chat.status === ChatStatus.USER_TURN;

  if (showAfterHistory || showAtEnd) {
    return { show: true, lastAssistantText: lastContent.text };
  }

  return { show: false };
}

interface ChatByIdViewProps {
  chat: ChatRecord;
  isCancelling?: boolean;
}

export const ChatByIdView = ({
  chat,
  isCancelling = false,
}: ChatByIdViewProps) => {
  const { handleLinkClick, autoNavEnabled } = useAIChat();

  // Spacer hook for hoisting user messages to top of viewport
  const { scrollContainerRef, spacerRef, messageToHoistRef, recomputeSpacer } =
    useSpacer();

  // Scroll sentinel hook for detecting when user has scrolled up
  const scrollSentinel = useScrollSentinel(scrollContainerRef);

  // Track user message count to detect when a NEW user message is added
  const userMessageCountRef = React.useRef<number>(0);

  // Track first run to distinguish mount from subsequent updates
  const isFirstRunRef = React.useRef(true);

  // Flag to suppress scroll button flicker during auto-scroll
  const [isAutoScrolling, setIsAutoScrolling] = React.useState(false);

  // Recompute spacer when messages change (streaming, new messages)
  React.useLayoutEffect(recomputeSpacer, [chat.messages, recomputeSpacer]);

  // Handle scroll on mount and when new user messages are added
  React.useLayoutEffect(
    function scrollOnMessagesChange() {
      const currentUserMessageCount = chat.messages.filter(
        (m) => m.role === ChatRole.USER,
      ).length;

      if (isFirstRunRef.current) {
        // First mount: initialize count and scroll to bottom
        isFirstRunRef.current = false;
        userMessageCountRef.current = currentUserMessageCount;

        // Only scroll after browser paint to ensure layout has fully applied
        requestAnimationFrame(function scrollToBottom() {
          scrollSentinel.ref.current?.scrollIntoView({
            block: "end",
            behavior: "instant",
          });
        });

        // Skip the rest on first run
        return;
      }

      if (currentUserMessageCount > userMessageCountRef.current) {
        // New user message: scroll to it with smooth animation
        userMessageCountRef.current = currentUserMessageCount;

        // Suppress button during scroll animation
        setIsAutoScrolling(true);

        messageToHoistRef.current?.scrollIntoView({
          block: "start",
          behavior: "smooth",
        });

        // Clear flag after scroll animation completes (estimate ~300ms)
        setTimeout(() => {
          setIsAutoScrolling(false);
        }, 300);
      }
    },
    [chat.messages, messageToHoistRef, scrollSentinel.ref],
  );

  const thinking = React.useMemo(() => {
    if (chat.messages.length === 0) {
      return true;
    }

    if (chat.status === ChatStatus.USER_TURN) {
      return false;
    }

    const lastMessage = chat.messages.at(-1);

    // If we're currently addressing a tool use request, that has its own loading indicator, so we shouldn't
    // double-display them
    if (lastMessage) {
      const lastContent = lastMessage?.content.at(-1);

      if (isChatToolUseContent(lastContent)) {
        return false;
      }
    }

    return lastMessage?.status === ChatMessageStatus.COMPLETED;
  }, [chat]);

  // Find the index of the last user message for ref attachment
  const lastUserMessageIndex = React.useMemo(() => {
    for (let i = chat.messages.length - 1; i >= 0; i--) {
      if (chat.messages[i].role === ChatRole.USER) {
        return i;
      }
    }
    return -1;
  }, [chat.messages]);

  const isGenerating =
    chat.messages.length > 0 && chat.status === ChatStatus.ROBOTO_TURN;

  return (
    <div ref={scrollContainerRef} className={styles.scrollContainer}>
      <div className={styles.centeringContainer}>
        <div
          className={styles.contentContainer}
          role="log"
          aria-label="Chat conversation"
          aria-live="polite"
        >
          {chat.messages.length > 0 &&
            chat.messages.map((message, index) => {
              const feedback = shouldShowFeedback(chat, index);
              const isLastMessage = index === chat.messages.length - 1;
              const isLastUserMessage = index === lastUserMessageIndex;

              return (
                <article
                  key={index}
                  ref={isLastUserMessage ? messageToHoistRef : undefined}
                  className={styles.messageArticle}
                >
                  <ChatMessageComponents
                    message={message}
                    isLastMessageInChat={isLastMessage}
                    onLinkClick={handleLinkClick}
                    autoNavDedupeKey={`${chat.chat_id}-${index}`}
                    autoNavEnabled={
                      autoNavEnabled && isMessageRecentForAutoNav(message)
                    }
                    isCancelling={isCancelling}
                  />
                  {feedback.show && (
                    <ChatFeedback
                      chatId={chat.chat_id}
                      messageIndex={index}
                      lastAssistantText={feedback.lastAssistantText}
                    />
                  )}
                </article>
              );
            })}

          {/* Thinking indicator */}
          {thinking && <ThinkingIndicator />}

          {/* Scroll sentinel - detects when scrolled to bottom of content */}
          <div
            ref={scrollSentinel.ref}
            aria-hidden="true"
            style={{ height: "1px", width: "100%", pointerEvents: "none" }}
          />

          {/* Spacer at bottom - height set via direct DOM manipulation */}
          <div ref={spacerRef} aria-hidden="true" style={{ height: 0 }} />
        </div>
      </div>

      <div className={styles.scrollToBottomButtonContainer}>
        <ScrollToBottomButton
          show={!scrollSentinel.isVisible && !isAutoScrolling}
          loading={isGenerating}
          onClick={function scrollToBottom() {
            scrollSentinel.ref.current?.scrollIntoView({
              block: "end",
              behavior: "instant",
            });
          }}
        />
      </div>
    </div>
  );
};
